import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { VENDOR_STAGES_ALL, gbp, ukDate } from "./crmPipelineShared";
import { useStageMover } from "./useStageMover";
import { SourceBadge } from "./SourceBadge";
import { LoadError, errMsg } from "./LoadError";

function ReviewRow({ r, onDone }) {
  const [note, setNote] = useState("");
  const mover = useStageMover({ view: "vendor", onMoved: onDone });
  const confirm = async () => {
    try { await api.post(`/crm/stage-review/${r.store}/${r.id}/clear`, { note }); toast.success(`Reviewed — stage confirmed as ${r.stage}`); onDone(); }
    catch (e) { toast.error(errMsg(e, "Could not save review")); }
  };
  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-4" data-testid={`review-row-${r.id}`}>
      {mover.dialog}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to={r.store === "lead" ? `/crm/vendor-leads/${r.id}` : `/crm/contact/${r.store}/${r.id}`} className="text-white font-medium hover:text-brass">{r.name}</Link>
          <div className="text-[11px] text-emerald-100/50 flex items-center gap-2 mt-0.5"><SourceBadge source={r.source} /> {r.email || r.phone || "—"} · added {ukDate(r.created_at)}</div>
        </div>
        <div className="text-[11px] text-emerald-100/60 text-right">
          <div>Original stage: <b className="text-white" data-testid={`review-legacy-${r.id}`}>{r.legacy_stage || "—"}</b>{r.legacy_status ? ` · status ${r.legacy_status}` : ""}</div>
          {r.legacy_deal && <div>Old deal record: {r.legacy_deal.transaction_type} · {gbp(r.legacy_deal.price)} · {ukDate(r.legacy_deal.completion_date)} (unverified)</div>}
        </div>
      </div>
      <p className="text-xs text-amber-200/80 mt-2">{r.reason}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-emerald-100/50">Current stage:</span>
        <select value={r.stage} onChange={(e) => mover.request({ store: r.store, id: r.id }, e.target.value)} data-testid={`review-stage-${r.id}`}
          className="bg-[#091E16] border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white">{VENDOR_STAGES_ALL.map((s) => <option key={s}>{s}</option>)}</select>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Review note (optional) — e.g. confirmed with vendor by phone" data-testid={`review-note-${r.id}`}
          className="flex-1 min-w-[200px] bg-[#091E16] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white" />
        <button onClick={confirm} data-testid={`review-confirm-${r.id}`} className="flex items-center gap-1.5 text-sm bg-brass text-emerald-950 font-semibold rounded-lg px-3 py-1.5"><CheckCircle2 className="w-4 h-4" /> Confirm stage & mark reviewed</button>
      </div>
    </div>
  );
}

export default function CrmStageReview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(async () => {
    setErr(false);
    try { const { data } = await api.get("/crm/stage-review-queue"); setD(data); } catch (e) { setErr(true); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <CrmShell title="Stage Review">
      <p className="text-sm text-emerald-100/60 mb-4 max-w-3xl">These leads came from the old pipeline, and their stage couldn't be worked out safely. Their original stage and history are kept. Set the correct stage (or keep the current one), then confirm. Nothing here is treated as a completed sale or purchase until you confirm it.</p>
      {err && <LoadError message="Review queue could not be loaded." onRetry={load} testId="review-error" />}
      {d && d.records.length === 0 && <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-8 text-center text-emerald-100/60" data-testid="review-empty">All caught up — no leads need review.</div>}
      <div className="space-y-3">{(d?.records || []).map((r) => <ReviewRow key={`${r.store}-${r.id}`} r={r} onDone={load} />)}</div>
    </CrmShell>
  );
}
